import * as React from 'react';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { Thumbnail } from 'common/components/cookbook/Thumbnail';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { InventoryQuantity } from 'features/warehouse/common/components/InventoryQuantity';

const Root = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${color.border};
`;

const InboundLoadRow = styled.div`
  border-bottom: 1px solid ${color.border};
  padding-bottom: 12px;
  margin-bottom: 16px;
`;

const InboundLoadText = styled(T.captionLabel)`
  color: ${color.textSubdued};
`;

const ItemInformationRow = styled.div`
  display: flex;
`;

const LeftColumn = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RightColumnRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  & + & {
    margin-top: 4px;
  }
`;

const Name = styled.span`
  font-weight: 500;
`;

const Gtin = styled.span`
  margin-left: 8px;
`;

const IconContainer = styled.div`
  margin-right: 8px;
`;

const IncreasedBy = styled.span`
  margin-right: 8px;
`;

type Props = {
  productName: string;
  productGtin?: string;
  productImage?: string;
  quantity: number;
  locationName?: string;
  inboundLoadIdentifier?: string;
  ref?: React.Ref<HTMLDivElement>;
};

export const GoodsReceiptRow = ({
  productName,
  productGtin,
  productImage,
  quantity,
  locationName,
  inboundLoadIdentifier,
  ref,
}: Props) => {
  const formatMessage = useFormatMessage();
  return (
    <Root verticalPadding="sm" ref={ref}>
      {!!inboundLoadIdentifier && (
        <InboundLoadRow>
          <InboundLoadText>{inboundLoadIdentifier}</InboundLoadText>
        </InboundLoadRow>
      )}
      <ItemInformationRow>
        <LeftColumn>
          <Thumbnail imgSrc={productImage} altText={productName} />
        </LeftColumn>
        <RightColumn>
          <RightColumnRow>
            <Name data-test-id="goods-receipt-name">{productName}</Name>
          </RightColumnRow>
          <RightColumnRow>
            <IconContainer>
              <Icon source={icons.barcode} size="small" fill={color.text} />
            </IconContainer>
            <Gtin data-test-id="goods-receipt-gtin">
              {productGtin || formatMessage('common.not_available')}
            </Gtin>
          </RightColumnRow>
          <RightColumnRow>
            <Icon source={icons.plusCircleFilled} size="small" fill={color.textPositive} />
            <IncreasedBy>
              <InventoryQuantity
                quantity={quantity}
                measurementUnit="UNITS" // Only UNITS are supported in legacy tool
              />
            </IncreasedBy>
            <IconContainer>
              <Icon source={icons.arrowRight} size="small" fill={color.textPositive} />
            </IconContainer>
            <span data-test-id="goods-receipt-location">
              {locationName || formatMessage('receiving.unknown_location')}
            </span>
          </RightColumnRow>
        </RightColumn>
      </ItemInformationRow>
    </Root>
  );
};
